// Kas & Bank Classification — List view utk 3 tab (Penerimaan/Pembayaran/
// Transfer Bank) + sub-tab "Belum Diklasifikasi" (2026-02).
import { useCallback, useEffect, useMemo, useState } from "react";
import { PenLine, Tag, Search, FileDown, Layers, CheckSquare, Square, Plus, Landmark, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import { fetchMovements, KIND_META, downloadExport } from "@/lib/kasBankClassification";
import { listManualEntries, manualIdFromMovement } from "@/lib/kasBankManual";
import ClassificationDialog from "./ClassificationDialog";
import BulkClassifyDialog from "./BulkClassifyDialog";
import ManualEntryDialog from "./ManualEntryDialog";

const KIND_TITLE = {
  penerimaan: { label: "Penerimaan", desc: "Mutasi bank masuk yang sudah diklasifikasi sebagai Penerimaan." },
  pembayaran: { label: "Pembayaran", desc: "Mutasi bank keluar yang sudah diklasifikasi sebagai Pembayaran." },
  transfer:   { label: "Transfer Bank", desc: "Mutasi bank (masuk/keluar) yang ditandai sebagai Transfer antar akun sistem." },
  unclassified: { label: "Belum Diklasifikasi", desc: "Mutasi bank yang belum diberi klasifikasi. Klik untuk mengklasifikasi." },
};

export default function ClassificationListTab({ kind, defaultKind, canWrite = true, canDelete = true, canManualWrite = false, canManualDelete = false, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set()); // set of movement_id
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // 2026-09-23 (user request Input Transaksi Manual): dialog + peta entri.
  const [manualMap, setManualMap] = useState({}); // { entryId: entryDoc }
  const [manualDlg, setManualDlg] = useState(null); // { mode:"create"|"edit", entry? }
  // 2026-09-24 (user request): peta rekonsiliasi e-statement → {row_id: summary}
  // utk chip "Invoice Terkait" + warning selisih pada baris yang ter-link.
  const [recon, setRecon] = useState({});

  // Tab konteks utk Input Transaksi (hanya di 3 tab kanan; sub "belum diklasifikasi" tidak).
  const manualKind = kind === "unclassified" ? null : kind;

  const meta = KIND_TITLE[kind] || KIND_TITLE.unclassified;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchMovements(kind);
      setRows(data);
      // Chip rekonsiliasi: batch-fetch ringkasan alokasi utk baris yang ter-link e-statement.
      const estIds = [...new Set(data.map((r) => r.estatement_row_id).filter(Boolean))];
      if (estIds.length) {
        try {
          const { data: sum } = await api.post("/kas-bank/reconciliation/rows-summary", { row_ids: estIds });
          setRecon(sum?.rows || {});
        } catch { setRecon({}); }
      } else {
        setRecon({});
      }
      // Bila tab manual, ambil peta entri manual utk edit dialog.
      if (manualKind) {
        try {
          const entries = await listManualEntries(manualKind);
          const map = {};
          for (const e of entries) map[e.id] = e;
          setManualMap(map);
        } catch { setManualMap({}); }
      } else {
        setManualMap({});
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal memuat data.");
    } finally { setLoading(false); }
  }, [kind, manualKind]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) => [r.description, r.project_name, r.account_bank_name, r.account_no, r.klasifikasi?.doc_no, r.klasifikasi?.coa_name, r.klasifikasi?.coa_code, r.klasifikasi?.note]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [rows, q]);

  // Auto-bersihkan seleksi yang sudah tidak ada di data (mis. setelah reload).
  useEffect(() => {
    setSelected((prev) => {
      const validIds = new Set(filtered.map((r) => r.movement_id).filter(Boolean));
      const next = new Set();
      prev.forEach((id) => { if (validIds.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  const selectableRows = useMemo(() => filtered.filter((r) => r.movement_id && canWrite), [filtered, canWrite]);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.movement_id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableRows.map((r) => r.movement_id)));
  };
  const toggleRow = (mid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(mid)) next.delete(mid); else next.add(mid);
    return next;
  });
  const selectedRows = useMemo(() => filtered.filter((r) => selected.has(r.movement_id)), [filtered, selected]);

  const total = useMemo(() => filtered.reduce((a, r) => a + (Number(r.amount) || 0), 0), [filtered]);

  const handleSaved = () => {
    load();
    onChanged?.();
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await downloadExport(kind);
      toast.success("Ekspor Excel diunduh.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal export Excel.");
    } finally { setExporting(false); }
  };

  return (
    <div className="bg-white border border-slate-200" data-testid={`kbc-tab-${kind || "all"}`}>
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
        <Tag size={15} className="text-[#0B1D3A]" />
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700">{meta.label}</div>
        <span className="text-[10px] text-slate-400 italic">{meta.desc}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari deskripsi/proyek/bank/dokumen" data-testid="kbc-search"
              className="pl-6 pr-2 py-1 border border-slate-300 text-[11px] bg-white w-64 focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
          </div>
          {canWrite && (
            <button type="button" onClick={() => setBulkOpen(true)} disabled={selected.size === 0} data-testid="kbc-bulk-open"
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FF5A1F] hover:bg-[#e34d16] disabled:opacity-40 text-white text-[10px] uppercase tracking-wider font-semibold transition-colors">
              <Layers size={11} /> Klasifikasi Massal ({selected.size})
            </button>
          )}
          {/* 2026-09-24 (user request): tombol "Input Transaksi" dihapus dari
              tab Penerimaan/Pembayaran/Transfer — data diinput & dipilih dari
              tab lain (Input & Categorize / e-statement). Edit entri manual
              existing tetap tersedia via ikon pensil di baris. */}
          <button type="button" onClick={handleExport} disabled={exporting} data-testid="kbc-export"
            title="Ekspor Excel semua baris pada tab ini (sesuai filter kind)"
            className="inline-flex items-center gap-1 px-2.5 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 text-[10px] uppercase tracking-wider font-semibold transition-colors">
            <FileDown size={11} /> {exporting ? "Menyiapkan…" : "Ekspor Excel"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50/60">
        <div className="bg-white border border-slate-200 px-3 py-2" data-testid="kbc-total-rows">
          <div className="overline text-[9px] text-slate-500">Jumlah Mutasi</div>
          <div className="font-display text-base font-bold tabular-nums text-[#0B1D3A]">{filtered.length}</div>
        </div>
        <div className="bg-white border border-slate-200 px-3 py-2 md:col-span-2" data-testid="kbc-total-sum">
          <div className="overline text-[9px] text-slate-500">Total Nominal</div>
          <div className="font-display text-base font-bold tabular-nums text-[#0B1D3A]">{formatIDR(total)}</div>
        </div>
        <div className="bg-white border border-slate-200 px-3 py-2" data-testid="kbc-total-note">
          <div className="overline text-[9px] text-slate-500">Sumber Kategori</div>
          <div className="text-[11px] font-semibold text-slate-700">Master COA</div>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-xs italic text-slate-400" data-testid="kbc-loading">Memuat data…</div>
      ) : error ? (
        <div className="p-6 text-center text-[11px] text-rose-700 bg-rose-50" data-testid="kbc-error">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center text-[11px] text-slate-400" data-testid="kbc-empty">
          {kind === "unclassified" ? "Tidak ada mutasi yang belum diklasifikasi." : `Belum ada mutasi berjenis ${meta.label}.`}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="kbc-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-2 py-2 w-8">
                  {canWrite && selectableRows.length > 0 && (
                    <button type="button" onClick={toggleAll} data-testid="kbc-select-all"
                      title={allSelected ? "Batalkan semua" : "Pilih semua"}
                      className="inline-flex items-center justify-center text-slate-600 hover:text-[#0B1D3A]">
                      {allSelected ? <CheckSquare size={14} className="text-[#FF5A1F]" /> : <Square size={14} />}
                    </button>
                  )}
                </th>
                <th className="text-left px-3 py-2">Tanggal</th>
                <th className="text-left px-3 py-2">Deskripsi</th>
                <th className="text-left px-3 py-2">Proyek</th>
                <th className="text-left px-3 py-2">Rekening</th>
                <th className="text-left px-3 py-2">Klasifikasi</th>
                <th className="text-left px-3 py-2">No Dokumen</th>
                <th className="text-left px-3 py-2">Invoice Terkait</th>
                <th className="text-right px-3 py-2">Nominal</th>
                <th className="text-right px-3 py-2">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, idx) => {
                const k = r.klasifikasi?.kind;
                const km = k ? KIND_META[k] : null;
                const key = r.movement_id || `nom-${idx}`;
                const isSel = r.movement_id && selected.has(r.movement_id);
                return (
                  <tr key={key} className={`border-b border-slate-100 ${isSel ? "bg-amber-50/60" : "hover:bg-slate-50"}`} data-testid={`kbc-row-${r.movement_id || idx}`}>
                    <td className="px-2 py-2">
                      {r.movement_id && canWrite ? (
                        <button type="button" onClick={() => toggleRow(r.movement_id)} data-testid={`kbc-select-${r.movement_id}`}
                          className="inline-flex items-center justify-center text-slate-500 hover:text-[#FF5A1F]">
                          {isSel ? <CheckSquare size={13} className="text-[#FF5A1F]" /> : <Square size={13} />}
                        </button>
                      ) : (
                        <span className="text-[9px] text-slate-300" title="Legacy tanpa ID stabil">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">{fmtDate(r.date) || "—"}</td>
                    <td className="px-3 py-2"><div className="font-medium text-slate-800">{r.description || "—"}</div>
                      <div className="text-[10px] text-slate-500">{r.direction === "in" ? "Mutasi Masuk" : "Mutasi Keluar"} · {r.source || "—"}</div></td>
                    <td className="px-3 py-2">{r.klasifikasi?.project_name || r.project_name || "—"}</td>
                    <td className="px-3 py-2"><div>{r.account_bank_name || "—"}</div><div className="text-[10px] tabular-nums text-slate-500">{r.account_no || "—"}</div></td>
                    <td className="px-3 py-2">
                      {km ? (
                        <div>
                          <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold border ${km.classes}`}>{km.label}</span>
                          <div className="text-[10px] text-slate-600 mt-0.5">{r.klasifikasi?.coa_code ? `${r.klasifikasi.coa_code} · ${r.klasifikasi.coa_name}` : "Tanpa kategori"}</div>
                          {r.klasifikasi?.note && <div className="text-[10px] text-slate-500 italic mt-0.5">{r.klasifikasi.note}</div>}
                        </div>
                      ) : (
                        <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold bg-slate-100 text-slate-600 border border-slate-200">Belum</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{r.klasifikasi?.doc_no || "—"}</td>
                    <td className="px-3 py-2" data-testid={`kbc-recon-${r.movement_id || idx}`}>
                      {r.estatement_row_id && recon[r.estatement_row_id] ? (() => {
                        const m = recon[r.estatement_row_id];
                        const others = (m.allocations || []).filter((a) => a.receipt_id !== r.reference_no);
                        const sisa = Math.round((m.remaining || 0) * 100) / 100;
                        return (
                          <div className="space-y-0.5">
                            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-[9px] uppercase tracking-wider font-semibold">
                              <Landmark size={9} /> Mutasi Bank · {fmtDate(m.date)} · {formatIDR(m.amount)}
                            </div>
                            {others.map((a, i) => (
                              <div key={i} className="text-[9px] text-slate-500">↳ {a.invoice_no || fmtDate(a.invoice_date) || "Invoice"} · {a.project_name} · {formatIDR(a.amount)}</div>
                            ))}
                            {Math.abs(sisa) > 0.01 && (
                              <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 border border-amber-300 text-amber-800 text-[9px] font-semibold" data-testid={`kbc-recon-warn-${r.movement_id || idx}`}>
                                <AlertTriangle size={9} /> Selisih {formatIDR(sisa)}
                              </div>
                            )}
                          </div>
                        );
                      })() : <span className="text-slate-300">—</span>}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.direction === "in" ? "text-emerald-700" : "text-rose-700"}`}>{formatIDR(r.amount || 0)}</td>
                    <td className="px-3 py-2 text-right">
                      {r.is_manual && manualKind ? (
                        <button type="button" onClick={() => {
                          const eid = manualIdFromMovement(r.movement_id);
                          const ent = eid ? manualMap[eid] : null;
                          if (ent) setManualDlg({ mode: "edit", entry: ent });
                          else toast.error("Entri manual tidak ditemukan.");
                        }} disabled={!canManualWrite && !canManualDelete}
                          data-testid={`kbm-edit-${r.movement_id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FF5A1F] hover:bg-[#e34d16] disabled:opacity-40 text-white text-[10px] uppercase tracking-wider font-semibold transition-colors">
                          <PenLine size={11} /> Ubah Manual
                        </button>
                      ) : (
                        <button type="button" onClick={() => setEditing(r)} disabled={!r.movement_id || !canWrite}
                          data-testid={`kbc-edit-${r.movement_id || idx}`}
                          title={!r.movement_id ? "Mutasi legacy tanpa ID stabil" : (canWrite ? "Klasifikasi mutasi" : "Tidak berhak")}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#12284d] disabled:opacity-40 text-white text-[10px] uppercase tracking-wider font-semibold transition-colors">
                          <PenLine size={11} /> {km ? "Ubah" : "Klasifikasi"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ClassificationDialog
          row={editing}
          defaultKind={defaultKind || (kind === "unclassified" ? null : kind)}
          canWrite={canWrite}
          canDelete={canDelete}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {bulkOpen && (
        <BulkClassifyDialog
          rows={selectedRows}
          defaultKind={defaultKind || (kind === "unclassified" ? null : kind)}
          onClose={() => setBulkOpen(false)}
          onSaved={() => { setBulkOpen(false); setSelected(new Set()); handleSaved(); }}
        />
      )}

      {manualDlg && manualKind && (
        <ManualEntryDialog
          kind={manualKind}
          entry={manualDlg.mode === "edit" ? manualDlg.entry : null}
          canDelete={canManualDelete}
          onClose={() => setManualDlg(null)}
          onSaved={() => { setManualDlg(null); handleSaved(); }}
        />
      )}
    </div>
  );
}
